import { useMemo } from 'react';
import { PokemonV2 } from '../pokemon/pokemon';
import { POKEMON_ITEM_ID } from '../types/item';
import { Move } from '../move/move';
import { StatInputs } from '../types/stat';
import { ItemOption } from '../types/item';
import { getAutoItemForPokemonId, isItemLockedForPokemonEntity } from '../lib/pokemon-config';
import type { PokemonOption } from '../types/pokemon';

// LeftSideBarで必要となる値とハンドラをひとまとめにするVM型
export interface LeftSideBarVM {
  // 選択中ポケモン
  selectedPokemon: PokemonV2 | null;
  setSelectedPokemon: React.Dispatch<React.SetStateAction<PokemonV2 | null>>;

  // ポケモン検索/選択
  pokemonOptions: PokemonOption[];
  handlePokemonSelect: (event: any, newValue: any) => void;

  // テラスタイプ/特性/道具
  selectedDefenseItem: POKEMON_ITEM_ID;
  setSelectedDefenseItem: (a: POKEMON_ITEM_ID) => void;
  handleSelectAbilityForDefensePokemon: (abilityId: number) => void;
  getDefenseItems: ItemOption[];
  isItemLocked: boolean; // UI側の無効化に利用

  // 努力値/性格/個体値
  statInputs: StatInputs;
  handleStatChange: (statName: keyof Pick<StatInputs, 'hp_effort' | 'attack_effort' | 'defense_effort' | 'special_attack_effort' | 'special_defense_effort' | 'speed_effort'>, value: number) => void;
  handlePersonalityChange: (statName: 'attack' | 'defense' | 'special_attack' | 'special_defense' | 'speed', value: number) => void;
  createIncrementHandler: (statName: keyof Pick<StatInputs, 'hp_effort' | 'attack_effort' | 'defense_effort' | 'special_attack_effort' | 'special_defense_effort' | 'speed_effort'>) => () => void;
  createDecrementHandler: (statName: keyof Pick<StatInputs, 'hp_effort' | 'attack_effort' | 'defense_effort' | 'special_attack_effort' | 'special_defense_effort' | 'speed_effort'>) => () => void;
  totalEvs: number;
  handleIndividualValueChange: (statName: 'hp' | 'attack' | 'defense' | 'special_attack' | 'special_defense' | 'speed', value: 0 | 31) => void;

  // 技選択
  selectedMoves: Move[];
  availableMoves: Move[];
  onMoveSelect: (moveIndex: number, move: Move | null) => void;
  getMoveDisplayName: (move: Move, pokemonId: number) => string;
  getMoveDisplayPower: (move: Move, pokemonId: number) => number | string;

  // 画面モード
  currentMode: 'defense' | 'attack' | 'speed';
  setCurrentMode: (mode: 'defense' | 'attack' | 'speed') => void;

  // 表示モード
  isComprehensiveMode: boolean;
  setIsComprehensiveMode: (nextMode: boolean) => void;
}

interface UseLeftSideBarViewModelArgs {
  selectedPokemon: PokemonV2 | null;
  setSelectedPokemon: React.Dispatch<React.SetStateAction<PokemonV2 | null>>;
  pokemonOptions: PokemonOption[];
  handlePokemonSelect: (event: any, newValue: any) => void;
  statInputs: StatInputs;
  selectedDefenseItem: POKEMON_ITEM_ID;
  setSelectedDefenseItem: (a: POKEMON_ITEM_ID) => void;
  handleStatChange: LeftSideBarVM['handleStatChange'];
  handlePersonalityChange: LeftSideBarVM['handlePersonalityChange'];
  createIncrementHandler: LeftSideBarVM['createIncrementHandler'];
  createDecrementHandler: LeftSideBarVM['createDecrementHandler'];
  totalEvs: number;
  handleSelectAbilityForDefensePokemon: (abilityId: number) => void;
  getDefenseItems: ItemOption[];
  handleIndividualValueChange: LeftSideBarVM['handleIndividualValueChange'];
  selectedMoves: Move[];
  availableMoves: Move[];
  onMoveSelect: LeftSideBarVM['onMoveSelect'];
  getMoveDisplayName: LeftSideBarVM['getMoveDisplayName'];
  getMoveDisplayPower: LeftSideBarVM['getMoveDisplayPower'];
  currentMode: LeftSideBarVM['currentMode'];
  setCurrentMode: LeftSideBarVM['setCurrentMode'];
  isComprehensiveMode: LeftSideBarVM['isComprehensiveMode'];
  setIsComprehensiveMode: LeftSideBarVM['setIsComprehensiveMode'];
}

// ViewModel作成用フック
export function useLeftSideBarViewModel(args: UseLeftSideBarViewModelArgs): LeftSideBarVM {
  const {
    selectedPokemon,
    setSelectedPokemon,
    pokemonOptions,
    handlePokemonSelect,
    statInputs,
    selectedDefenseItem,
    setSelectedDefenseItem,
    handleStatChange,
    handlePersonalityChange,
    createIncrementHandler,
    createDecrementHandler,
    totalEvs,
    handleSelectAbilityForDefensePokemon,
    getDefenseItems,
    handleIndividualValueChange,
    selectedMoves,
    availableMoves,
    onMoveSelect,
    getMoveDisplayName,
    getMoveDisplayPower,
    currentMode,
    setCurrentMode,
    isComprehensiveMode,
    setIsComprehensiveMode,
  } = args;

  // アイテム選択をロックすべきポケモンかどうか
  const isItemLocked = useMemo(() => {
    if (!selectedPokemon) return false;
    return isItemLockedForPokemonEntity(selectedPokemon);
  }, [selectedPokemon]);

  // 自動アイテム付与はLeftSideBar側のuseEffectで処理（既存ロジックを尊重）。
  // ここではロジックの所在をlibに寄せておくために関数をexportだけしておく。
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _autoItemForPokemon = (pokemonId: number) => getAutoItemForPokemonId(pokemonId);

  return {
    selectedPokemon,
    setSelectedPokemon,
    pokemonOptions,
    handlePokemonSelect,
    selectedDefenseItem,
    setSelectedDefenseItem,
    handleSelectAbilityForDefensePokemon,
    getDefenseItems,
    isItemLocked,
    statInputs,
    handleStatChange,
    handlePersonalityChange,
    createIncrementHandler,
    createDecrementHandler,
    totalEvs,
    handleIndividualValueChange,
    selectedMoves,
    availableMoves,
    onMoveSelect,
    getMoveDisplayName,
    getMoveDisplayPower,
    currentMode,
    setCurrentMode,
    isComprehensiveMode,
    setIsComprehensiveMode,
  };
}
